/**
 * English translation dictionary — default locale.
 *
 * Keys are dot-paths like `data.upload.title`. Interpolation uses
 * `{name}` placeholders matched against the values dict passed to `t`.
 *
 * Adding a new locale = mirror this file's keys. Missing keys fall back
 * to English so the app never shows a translation key in the UI.
 */

export const en = {
  // ---------------- Trial / billing -------------------------------------
  trial: {
    badge_active: "{days} days left in trial · {used}/{total} questions",
    badge_nudge: "{remaining} questions left — upgrade to keep going",
    badge_warning: "Trial nearly over · {remaining} questions left",
    badge_blocked: "Trial expired — upgrade to keep using Ask Meridian",
    badge_paid: "Pro plan",
    upgrade_button: "Upgrade",
    quota_modal_title: "You're hooked — keep going",
    quota_modal_body: "Upgrade your workspace to remove the trial limit and unlock unlimited Ask Meridian, branches, and scenarios.",
  },

  // ---------------- Generic / shared ------------------------------------
  common: {
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    delete: "Delete",
    deleting: "Deleting…",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    skip: "Skip",
    loading: "Loading…",
    retry: "Retry",
    edit: "Edit",
    rename: "Rename",
    open: "Open",
    add: "Add",
    close: "Close",
    yes: "Yes",
    no: "No",
    error_generic: "Something went wrong",
    just_now: "just now",
    minutes_ago: "{n} min ago",
    hours_ago: "{n} h ago",
    days_ago: "{n} d ago",
  },

  // ---------------- Top-level navigation -------------------------------
  // The primary nav reads "Today / Plans / Models" — three task-based
  // pillars instead of the older five entity-based tabs. Ask Meridian
  // moved into a persistent AskBar; Develop demoted into the avatar
  // dropdown. Old keys kept (home, data, ask_meridian, develop) for
  // page-internal copy that still references the old labels.
  home: {
    scaffold_plan_link: "Jump back into your plan",
  },

  nav: {
    today: "Today",
    home: "Home", // legacy — still used by page eyebrows etc.
    data: "Data", // legacy
    models: "Models",
    ask_meridian: "Ask Meridian",
    develop: "Develop",
    plans: "Plans",
    administration: "Administration",
  },

  // ---------------- Data tab -------------------------------------------
  data: {
    title: "Data",
    subtitle: "Upload a file — Meridian structures it into a model you can ask questions of.",
    upload: {
      drop_here: "Drop a file here",
      or_click: "or click to browse",
      uploading: "Uploading…",
      analyzing: "Analyzing…",
      hint_formats: "CSV, XLSX, or JSON · max {size} MB",
    },
    datasets_section: "Datasets",
    datasets_count: "{n} total",
    datasets_empty: "No datasets uploaded yet. Drop a file above to get started.",
    delete_confirm: "Delete {name}?",
    status: {
      uploaded: "Uploaded",
      analyzing: "Analyzing",
      ready: "Ready",
      failed: "Failed",
    },
    clarify: {
      title: "Before I build the model",
      reading_schema: "Reading your schema…",
      no_questions: "The schema looks unambiguous — I can build the model directly.",
      create_model: "Create model",
      create_with_answers: "Create model with these answers",
      add_to_existing: "Add to model",
    },
    destination: {
      label: "Where should this data land?",
      new_model: "New model",
      new_model_desc: "Build a brand-new model from this file.",
      attach_existing: "Add to existing",
      attach_existing_desc: "Stitch onto a model you already built — Meridian proposes the joins.",
    },
    answer_placeholder: "Your answer…",
  },

  // ---------------- Ask Meridian tab -----------------------------------
  ask: {
    title: "Ask Meridian",
    subtitle_with_model: "Ask a question against {model}.",
    subtitle_without_model: "Ask a question against your model.",
    placeholder: "Ask anything about your data…",
    placeholder_disabled: "Pick a branch first…",
    thinking: "Thinking…",
    pick_model: "Pick a model to ask questions of.",
    no_models: "No models yet — go to {data_link} and upload a file.",
    fetching_models: "Fetching models…",
    asking_against: "asking against {model}",
    no_branch: "Couldn't reach the live model. Try reloading, or check its data sources under Data.",
    suggestions: {
      revenue_q3: "Show Q3 revenue by region",
      forecast_quarter: "What will revenue be next quarter?",
      what_if_price: "What if we raise the price 5%?",
      deals_march: "Which deals will likely close in March?",
    },
    new_chat: "New chat",
  },

  // ---------------- Develop tab ----------------------------------------
  develop: {
    title: "Develop",
    subtitle: "Branches, scenarios, and merge requests per model. Pick a model to open the graph.",
    no_models: "No models yet",
    no_models_hint: "Upload a file in the Data tab — Meridian creates your first model.",
    go_to_data: "Go to Data",
    fetch_failed: "Couldn't fetch models",
    created_at: "Created {when}",
    open_graph: "Open graph →",
    actions_label: "Model actions",
    rename_label: "Rename",
    rename_save: "Save",
    delete_label: "Delete model",
    delete_confirm_title: "Delete {name}?",
    delete_confirm_body: "All branches, commits, and artifacts under the model will be removed. This cannot be undone.",
  },

  // ---------------- Branches view --------------------------------------
  branches: {
    title: "Branches",
    subtitle: "Live model with parallel dev branches. Click a branch for details.",
    fork_button: "New branch",
    loading: "Loading branches…",
    tab_branches: "Branches",
    tab_sources: "Data sources",
    tab_timeline: "Timeline",
    tab_insights: "Insights",
  },

  // ---------------- Data sources view ----------------------------------
  sources: {
    title: "Data sources",
    subtitle: "Sources feeding {model}. AI proposes joins — confirm or reject.",
    subtitle_no_model: "Sources feeding the model.",
    add_button: "Add source",
    fetching: "Fetching sources and joins…",
    empty_title: "No data sources yet",
    empty_hint: "Click \"Add source\" or upload a file in Data.",
    sidebar_sources: "Sources ({n})",
    sidebar_relationships: "Joins ({n})",
    sidebar_no_rels: "Add more sources so the AI can find joins.",
    sidebar_click_hint: "Click a line in the graph for details.",
    proposed_join: "Proposed join",
    confidence_label: "Confidence: {pct}%",
    accept: "Accept",
    reject: "Reject",
    accept_reject_hint: "Accept/reject is coming in the next version — for now Meridian shows all proposals.",
    dialog_title: "Add data source",
    dialog_pick: "Pick a dataset",
    dialog_no_available: "No spare datasets in this workspace.\nUpload a file in the Data tab first.",
    dialog_attaching: "Attaching…",
    dialog_attach: "Add",
    fetching_datasets: "Fetching datasets…",
  },
  chart_frame: {
    refine: "Refine",
    confidence_medium_default: "Some uncertainty in this chart — see the band for the range.",
    confidence_low_default: "Thin data — directional only. Ask a more specific question for tighter results.",
    forecast_suppressed: "Forecast suppressed — {reason}.",
    empty_zero_rows: "No data yet. Try one of the suggestions below.",
    empty_sparse: "Only {count} data points — showing as dots, not a trend.",
    sub_narrative_drill_hint: "Click any underlined number to drill in.",
  },
} as const;

/**
 * Translations type — structurally identical to `typeof en` but every
 * leaf string is widened from its literal type back to plain `string`.
 *
 * Why: `en` is declared `as const` so dot-path autocomplete works in
 * `t("trial.badge_active")`. But that same `as const` makes each value
 * its *own literal type*, which would force every other locale's
 * dictionary to be a byte-for-byte copy of the English strings — which
 * is the bug that prevented `sv.ts` from compiling.
 *
 * `DeepStringValues` keeps the key shape (autocomplete intact) but
 * relaxes leaf values so a Swedish translation can actually be Swedish.
 */
type DeepStringValues<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepStringValues<T[K]>;
};

export type Translations = DeepStringValues<typeof en>;
